import { normalizePhoneAddress, normalizePhoneE164 } from './phoneAddresses.js';

export const DEFAULT_COMPANY_MAIN_LINE = '+14045942667';
export const RETIRED_SMS_LINE = '+13072882778';

const E164_PATTERN = /^\+[1-9]\d{7,14}$/;
const THREAD_LINE_ERROR = 'Could not verify this conversation’s sending number. Choose a sending number from the Send menu.';

function parseGroupThreadId(externalThreadId) {
    if (!externalThreadId.startsWith('group:')) return null;
    const key = externalThreadId.slice('group:'.length);
    const routeSeparator = key.lastIndexOf(':');
    const participantsKey = routeSeparator < 0 ? key : key.slice(0, routeSeparator);
    const routeLine = routeSeparator < 0 ? '' : key.slice(routeSeparator + 1);
    const participants = participantsKey.split(',');
    if (participants.length < 2 || participants.some((phone) => !E164_PATTERN.test(phone))
        || new Set(participants).size < 2
        || (routeSeparator >= 0 && !E164_PATTERN.test(routeLine))) {
        return null;
    }
    return { routeLine };
}

// Fallbacks are evidence, never a default company number. Callers must supply the
// current org's active lines before a missing thread key can use that evidence.
export function resolveThreadSendingNumber(externalThreadId, sessionRouteLine = '', rawLog = [], ownedLines = []) {
    if (!externalThreadId) {
        const first = (Array.isArray(rawLog) ? rawLog : []).find((entry) => entry?.direction === 'outbound' && ['sms', 'mms'].includes(entry.channel));
        const candidate = normalizePhoneE164(sessionRouteLine || first?.sending_line || first?.from || '');
        return candidate && candidate !== RETIRED_SMS_LINE && ownedLines.some((line) =>
            line.sms_enabled !== false && !line.blockedReason && normalizePhoneE164(line.phone_number || line.address) === candidate
        ) ? candidate : '';
    }
    if (typeof externalThreadId !== 'string') return '';
    const threadId = externalThreadId.trim().replace(/^conversations:/, '');
    if (threadId.startsWith('group:')) {
        const group = parseGroupThreadId(threadId);
        if (!group) return '';
        const routeLine = normalizePhoneE164(sessionRouteLine || group.routeLine);
        return routeLine && routeLine !== RETIRED_SMS_LINE ? routeLine : DEFAULT_COMPANY_MAIN_LINE;
    }
    const match = /^(\+[1-9]\d{7,14}):(\+[1-9]\d{7,14})$/.exec(threadId);
    return match ? normalizePhoneAddress(match[2]) : '';
}

export function resolveReplyThreadSendingNumber(externalThreadId, sessionRouteLine = '', rawLog = [], ownedLines = []) {
    const number = resolveThreadSendingNumber(externalThreadId, sessionRouteLine, rawLog, ownedLines);
    return number
        ? { ok: true, number, status: 200, error: null }
        : { ok: false, number: '', status: 422, error: THREAD_LINE_ERROR };
}

export function extractThreadRecipientAddress(externalThreadId) {
    if (typeof externalThreadId !== 'string' || externalThreadId.startsWith('group:')) return '';
    const separatorIndex = externalThreadId.indexOf(':');
    if (separatorIndex === -1 || separatorIndex === externalThreadId.length - 1) return '';
    return normalizePhoneAddress(externalThreadId.slice(separatorIndex + 1));
}

export function pickDefaultSendingIdentity(identities, channel, threadNumber = '') {
    const eligible = (identities || []).filter((identity) => (
        identity?.channel === channel
        && !identity.blockedReason
        && (!['sms', 'mms'].includes(channel)
            || (!identity.isRetiredSmsLine && normalizePhoneE164(identity.address) !== RETIRED_SMS_LINE))
    ));
    if (['sms', 'mms'].includes(channel)) {
        const threadId = typeof threadNumber === 'string' ? threadNumber : '';
        const hasThreadContext = threadId.length > 0;
        const threadE164 = threadId.includes(':')
            ? resolveThreadSendingNumber(threadId)
            : normalizePhoneE164(threadId);
        if (hasThreadContext && !threadE164) return null;
        if (threadE164) {
            return eligible.find((identity) => normalizePhoneE164(identity.address) === threadE164) ?? null;
        }
        return eligible.find((identity) => normalizePhoneE164(identity.address) === DEFAULT_COMPANY_MAIN_LINE) ?? null;
    }
    return eligible.find((identity) => identity.isDefaultForBrand)
        || eligible.find((identity) => identity.isMainLine)
        || eligible[0]
        || null;
}
